<template>
  <!-- Floating placements teleport to body (vars don't cascade through Teleport,
       so `theme` is bound on the pill). A compact 3-icon segmented control:
       Map + Filter open the browse overlay; Heart filters the host list in place. -->
  <Teleport v-if="placement !== 'inline'" to="body">
    <div class="filter-pill" :class="`filter-pill--${placement}`" :style="theme">
      <button
        id="pass-pill-map"
        type="button"
        class="filter-pill__seg gtm-click"
        aria-label="Map"
        @click="emit('open-map')"
      >
        <MapIcon :size="16" />
      </button>
      <button
        id="pass-pill-filter"
        type="button"
        class="filter-pill__seg gtm-click"
        aria-label="Search & filter"
        @click="emit('open-filter')"
      >
        <SlidersHorizontal :size="16" />
      </button>
      <button
        id="pass-pill-favorites"
        type="button"
        class="filter-pill__seg filter-pill__seg--heart gtm-click"
        :class="{ 'is-active': favoritesActive }"
        :aria-pressed="favoritesActive"
        :aria-label="favoritesActive ? 'Show all businesses' : 'Show favorites only'"
        @click="emit('toggle-favorites')"
      >
        <Heart :size="16" :fill="favoritesActive ? 'currentColor' : 'none'" />
        <span v-if="favoritesCount > 0" class="filter-pill__count">{{ favoritesCount }}</span>
      </button>
    </div>
  </Teleport>
</template>

<script setup>
import { Map as MapIcon, SlidersHorizontal, Heart } from '@lucide/vue'

/**
 * FilterPill — the floating discovery control on a pass. Three segments:
 *  • Map    → emits `open-map`        (host opens the browse overlay / map view)
 *  • Filter → emits `open-filter`     (host opens the browse overlay)
 *  • Heart  → emits `toggle-favorites` (host filters its own list in place)
 */
defineProps({
  /** Style object of --fr-* CSS vars — required on floating (teleported) placements. */
  theme: { type: Object, default: null },
  /** 'bottom-right' (default) | 'bottom-left'. */
  placement: { type: String, default: 'bottom-right' },
  /** Whether the favorites-only filter is currently on (highlights the heart). */
  favoritesActive: { type: Boolean, default: false },
  /** Favorites count shown on the heart. */
  favoritesCount: { type: Number, default: 0 }
})

const emit = defineEmits(['open-map', 'open-filter', 'toggle-favorites'])
</script>

<style lang="scss" scoped>
.filter-pill {
  pointer-events: auto;
  display: inline-flex;
  align-items: stretch;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--fr-surface, #ffffff) 95%, transparent);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--fr-ink, #1c1c1e) 18%, transparent),
    0 2px 8px 0 rgb(0 0 0 / 0.12);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  overflow: hidden;

  &--bottom-right {
    position: fixed;
    bottom: calc(env(safe-area-inset-bottom) + 0.75rem);
    right: max(0.75rem, calc((100vw - var(--project-max-width, 480px)) / 2 + 0.75rem));
    z-index: 2300;
  }
  &--bottom-left {
    position: fixed;
    bottom: calc(env(safe-area-inset-bottom) + 0.75rem);
    left: max(0.75rem, calc((100vw - var(--project-max-width, 480px)) / 2 + 0.75rem));
    z-index: 2300;
  }

  // Each segment: comfortable hit target, divided by a hairline.
  &__seg {
    position: relative;
    appearance: none;
    background: none;
    border: 0;
    margin: 0;
    font: inherit;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
    min-width: 46px;
    height: 42px;
    padding: 0 12px;
    color: var(--fr-ink, #1c1c1e);
    transition: background 0.15s, color 0.15s, transform 0.1s;

    & + & { box-shadow: inset 1px 0 0 color-mix(in srgb, var(--fr-ink, #1c1c1e) 12%, transparent); }
    &:hover { background: color-mix(in srgb, var(--fr-ink, #1c1c1e) 6%, transparent); }
    &:active { transform: scale(0.94); }

    :deep(svg) { color: var(--fr-primary, #FF9900); }

    // Heart active → favorites filter on: accent fill.
    &--heart.is-active {
      background: var(--fr-primary, #FF9900);
      :deep(svg) { color: var(--fr-primary-foreground, #fff); }
    }
  }

  &__count {
    font-family: var(--fr-font-display, inherit);
    font-size: 11px;
    font-weight: 800;
    font-variant-numeric: tabular-nums;
    color: var(--fr-ink, #1c1c1e);
  }
  &__seg--heart.is-active .filter-pill__count { color: var(--fr-primary-foreground, #fff); }
}
</style>
